import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PaymentModal } from '../components/subscription/PaymentModal';
import { CancellationModal } from '../components/subscription/CancellationModal';
import { AdminChatWidget } from '../components/subscription/AdminChatWidget';
import { useSubscription, useTrialStatus } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { cancelSubscription, getPlans, PlanConfig, PLAN_DEFAULTS } from '../services/subscriptionService';
import { paymentApi, PaymentOrderStatus } from '../services/paymentApi';
import './Pages.css';

export const Subscription: React.FC = () => {
  const { user } = useAuth();
  const { subscription, isActive, daysRemaining, loading, error: subscriptionError, refresh } = useSubscription();
  const { isTrial, isExpired } = useTrialStatus();
  const [searchParams] = useSearchParams();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [cancellationModalOpen, setCancellationModalOpen] = useState(false);
  const [plans, setPlans] = useState<Record<string, PlanConfig>>(PLAN_DEFAULTS);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);

  useEffect(() => {
    getPlans().then(setPlans);
  }, []);

  // O retorno do checkout só consulta o pedido. A ativação ocorre exclusivamente no webhook PHP.
  useEffect(() => {
    const status = searchParams.get('status');
    const orderId = searchParams.get('order_id') || sessionStorage.getItem('pendingPaymentOrderId');
    if (status === 'failure') setPaymentMessage('O pagamento não foi concluído. Você pode tentar novamente.');
    if (status === 'pending') setPaymentMessage('Pagamento pendente. O acesso será atualizado após a confirmação do Mercado Pago.');
    if ((status !== 'success' && status !== 'approved') || !orderId || !user?.uid) return;

    let cancelled = false;
    let attempts = 0;
    setPaymentMessage('Aguardando confirmação segura do Mercado Pago...');
    const pollOrder = async () => {
      try {
        const order = await paymentApi<PaymentOrderStatus>('order', { id: orderId });
        if (cancelled) return;
        if (order.status === 'approved') {
          sessionStorage.removeItem('pendingPaymentOrderId');
          setPaymentMessage('Pagamento confirmado e assinatura atualizada.');
          await refresh();
          return;
        }
        if (['rejected', 'cancelled', 'refunded', 'charged_back', 'error'].includes(order.status)) {
          setPaymentMessage('O pagamento não foi aprovado. Se houve cobrança, entre em contato com o suporte.');
          return;
        }
      } catch (error) {
        console.error('Erro ao consultar pedido de pagamento:', error);
      }
      attempts += 1;
      if (attempts < 20 && !cancelled) window.setTimeout(pollOrder, 3000);
      else if (!cancelled) setPaymentMessage('A confirmação ainda está pendente. Atualize esta página em alguns instantes.');
    };
    void pollOrder();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user?.uid]);

  const handleCancelConfirm = async () => {
    if (!user?.uid) return;

    try {
      await cancelSubscription(user.uid);
      await refresh();
      window.alert('Assinatura cancelada com sucesso. Você manterá acesso até o fim da validade atual.');
    } catch (error: any) {
      console.error('Erro ao cancelar assinatura:', error);
      throw error; // Re-throw para o modal tratar
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="ns-page">
        <div className="ns-page-header">
          <div>
            <h2>Assinatura</h2>
            <p>Carregando informações da assinatura...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Assinatura</h2>
          <p>Gerencie sua assinatura e plano de pagamento</p>
        </div>
      </div>

      {paymentMessage && (
        <div role="status" className="ns-payment-info" style={{ marginBottom: 16 }}>
          <p>{paymentMessage}</p>
        </div>
      )}
      {subscriptionError && (
        <div role="alert" className="ns-payment-error" style={{ marginBottom: 16 }}>
          <p>{subscriptionError}</p>
          <Button variant="secondary" onClick={() => void refresh()}>Tentar novamente</Button>
        </div>
      )}

      <div className="ns-grid ns-grid-2">
        <Card title="Status da Assinatura" subtitle="Informações do seu plano atual">
          <div className="ns-subscription-status">
            {isTrial && (
              <div className="ns-status-badge ns-status-trial">
                <strong>Período de Teste</strong>
                <p>{daysRemaining} dias restantes</p>
              </div>
            )}

            {isActive && subscription?.status === 'active' && (
              <div className="ns-status-badge ns-status-active">
                <strong>Assinatura Ativa</strong>
                <p>
                  {subscription.paymentType === 'recurring'
                    ? 'Renovação automática'
                    : subscription.paymentType === 'one-time'
                      ? 'Pagamento único'
                      : subscription.paymentType === 'manual' ? 'Liberação pelo administrador' : 'Tipo de cobrança não informado'}
                </p>
              </div>
            )}

            {isExpired && (
              <div className="ns-status-badge ns-status-expired">
                <strong>Assinatura Expirada</strong>
                <p>Renove para continuar usando</p>
              </div>
            )}

            {subscription?.status === 'cancelled' && (
              <div className="ns-status-badge ns-status-cancelled">
                <strong>Assinatura Cancelada</strong>
                <p>Acesso até {formatDate(subscription.accessEndsAt)}</p>
              </div>
            )}

            <div className="ns-subscription-details">
              <div className="ns-detail-row">
                <span>Início do Trial:</span>
                <strong>{formatDate(subscription?.trialStartDate)}</strong>
              </div>
              <div className="ns-detail-row">
                <span>Fim do Trial:</span>
                <strong>{formatDate(subscription?.trialEndDate)}</strong>
              </div>
              {subscription?.subscriptionStartDate && (
                <div className="ns-detail-row">
                  <span>Assinatura desde:</span>
                  <strong>{formatDate(subscription.subscriptionStartDate)}</strong>
                </div>
              )}
              {subscription?.subscriptionEndDate && (
                <div className="ns-detail-row">
                  <span>
                    {subscription.paymentType === 'recurring'
                      ? 'Próximo vencimento:'
                      : subscription.paymentType === 'one-time'
                        ? 'Acesso pago até:'
                        : 'Data final registrada:'}
                  </span>
                  <strong>{formatDate(subscription.subscriptionEndDate)}</strong>
                </div>
              )}
              {subscription?.lastPaymentDate && (
                <div className="ns-detail-row">
                  <span>Último pagamento:</span>
                  <strong>{formatDate(subscription.lastPaymentDate)}</strong>
                </div>
              )}
              {subscription?.paymentType && (
                <div className="ns-detail-row">
                  <span>Tipo de Pagamento:</span>
                  <strong>
                    {subscription.paymentType === 'manual' ? 'Liberação administrativa' : subscription.paymentType === 'recurring' ? 'Recorrente' : 'Único'}
                  </strong>
                </div>
              )}
              {subscription?.cancelledAt && (
                <div className="ns-detail-row">
                  <span>Cancelado em:</span>
                  <strong>{formatDate(subscription.cancelledAt)}</strong>
                </div>
              )}
            </div>

            {/* Informação sobre processamento automático via webhook */}
            {isTrial && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <p style={{ fontSize: '14px', color: '#1e40af', marginBottom: '4px' }}>
                  💡 <strong>Já fez o pagamento?</strong> Aguarde alguns instantes. Seu pagamento será processado automaticamente.
                </p>

                <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px', marginBottom: '0' }}>
                  ⚡ Processamento automático via webhook
                </p>
              </div>
            )}
          </div>
        </Card>

        <div className="ns-plans-grid" style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginTop: '24px' }}>
          {Object.entries(plans).filter(([key]) => key !== 'monthly').map(([key, plan]) => {
            const isCurrentPlan = subscription?.planType === key || (key === 'advanced' && subscription?.planType === 'monthly');
            const isPopular = key === 'medium';

            return (
              <Card
                key={key}
                title={plan.name}
                subtitle={`R$ ${plan.price.toFixed(2).replace('.', ',')} / mês`}
              >
                <div className="ns-subscription-plan">
                  {isPopular && <div className="ns-popular-badge">Mais Popular</div>}
                  <div className="ns-plan-features">
                    <p className="ns-plan-limit">
                      <strong>{plan.limit >= 9000 ? 'Ilimitado' : plan.limit}</strong> pacientes
                    </p>
                    <ul>
                      <li>✓ Acesso completo à plataforma</li>
                      <li>✓ Gestão de pacientes</li>
                      <li>✓ Planos de refeição</li>
                      <li>✓ App para pacientes</li>
                      {key === 'advanced' && <li>✓ Prioridade no suporte</li>}
                    </ul>
                  </div>

                  <div className="ns-plan-actions">
                    {isCurrentPlan ? (
                      <Button variant="secondary" disabled style={{ width: '100%', opacity: 0.7 }}>
                        Plano Atual
                      </Button>
                    ) : (
                      <Button
                        onClick={async () => {
                          setSelectedPlan(key);
                          setPaymentModalOpen(true);
                        }}
                        style={{ width: '100%' }}
                      >
                        {isActive ? 'Mudar para este plano' : 'Assinar Agora'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div >

      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        plan={selectedPlan ? plans[selectedPlan] : null}
        planKey={selectedPlan}
      />

      <CancellationModal
        isOpen={cancellationModalOpen}
        onClose={() => setCancellationModalOpen(false)}
        onConfirm={handleCancelConfirm}
        subscription={subscription}
      />

      <AdminChatWidget />
    </div >
  );
};
